/**
 * DebtCard (Patch BL): list of non-mortgage debt items. Each item has a
 * type (credit card / student loan / auto loan / personal loan / other),
 * optional label, balance, and optional APR.
 *
 * Mortgages are NOT in this list, by design. They live on
 * RealEstateProperty with mortgage set, since amortization mechanics
 * differ from revolving consumer debt and the property-debt link is
 * cleaner that way.
 *
 * No interest auto-roll: unlike the mortgage's compute-on-render
 * balance, debt balances stay where the user sets them. Revolving debt
 * balances depend on spending behavior we don't model.
 */

import { useState } from 'react';
import { useAppStore } from '../state/store.js';
import { Decimal, ZERO } from '../engine/money.js';
import { formatUsd } from './Money.js';
import { DecimalInput } from './DecimalInput.js';
import type { DebtItem, DebtItemType } from '../engine/types.js';

const DEBT_TYPE_LABELS: Record<DebtItemType, string> = {
  credit_card: 'Credit card',
  student_loan: 'Student loan',
  auto_loan: 'Auto loan',
  personal_loan: 'Personal loan',
  other: 'Other',
};

const DEBT_TYPES: DebtItemType[] = ['credit_card', 'student_loan', 'auto_loan', 'personal_loan', 'other'];

export function DebtCard(): JSX.Element {
  const items = useAppStore(s => s.debtItems);
  const addDebtItem = useAppStore(s => s.addDebtItem);
  const updateDebtItem = useAppStore(s => s.updateDebtItem);
  const removeDebtItem = useAppStore(s => s.removeDebtItem);

  const [newType, setNewType] = useState<DebtItemType>('credit_card');
  const [newLabel, setNewLabel] = useState('');
  const [newBalance, setNewBalance] = useState('');
  const [newApr, setNewApr] = useState('');

  // Total of all debt balances. Shown at the bottom + feeds the
  // Liabilities pie on the Dashboard (Patch BM).
  const total = items.reduce((acc, d) => acc.plus(d.balanceUsd), ZERO);

  function addRow(): void {
    const bal = parseFloat(newBalance);
    if (!isFinite(bal) || bal < 0) {
      alert('Enter a balance (zero or positive).');
      return;
    }
    const item: Omit<DebtItem, 'id'> = {
      type: newType,
      balanceUsd: new Decimal(bal),
    };
    if (newLabel.trim() !== '') {
      item.label = newLabel.trim();
    }
    const aprNum = parseFloat(newApr);
    if (isFinite(aprNum) && aprNum > 0) {
      // APR entered as a percent (e.g. 24 = 24%); store as fraction.
      item.aprPct = new Decimal(aprNum).div(100);
    }
    addDebtItem(item);
    setNewType('credit_card');
    setNewLabel('');
    setNewBalance('');
    setNewApr('');
  }

  return (
    <div>
      <p className="muted" style={{ fontSize: 'var(--fs-small)', lineHeight: 1.6, marginTop: 0, marginBottom: 'var(--gap-m)' }}>
        Track non-mortgage debt so the Liabilities side of net worth is
        complete. Mortgages live under Real estate (not here) because
        they amortize on a schedule.
      </p>

      {items.length === 0 ? (
        <p className="muted" style={{ fontStyle: 'italic', marginBottom: 'var(--gap-m)' }}>
          No debt items yet. Add one below.
        </p>
      ) : (
        <table className="tbl" style={{ marginBottom: 'var(--gap-m)' }}>
          <thead>
            <tr>
              <th>Type</th>
              <th>Label</th>
              <th className="num">Balance</th>
              <th className="num">APR</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d.id}>
                <td>
                  <select
                    value={d.type}
                    onChange={e => updateDebtItem(d.id, { type: e.target.value as DebtItemType })}
                    style={{ fontSize: 'var(--fs-small)' }}
                  >
                    {DEBT_TYPES.map(t => (
                      <option key={t} value={t}>{DEBT_TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    value={d.label ?? ''}
                    onChange={ev => updateDebtItem(d.id, { label: ev.target.value || undefined })}
                    placeholder="optional"
                    style={{ width: 180, fontSize: 'var(--fs-small)' }}
                  />
                </td>
                <td className="num">
                  <DecimalInput
                    decimals={2}
                    step="100"
                    value={d.balanceUsd}
                    onChange={dec => updateDebtItem(d.id, { balanceUsd: dec })}
                    style={{ width: 110 }}
                  />
                </td>
                <td className="num">
                  <DecimalInput
                    decimals={2}
                    step="0.25"
                    value={d.aprPct ? d.aprPct.times(100) : ZERO}
                    onChange={dec => updateDebtItem(d.id, { aprPct: dec.div(100) })}
                    style={{ width: 70 }}
                  />
                  <span className="faint" style={{ marginLeft: 4 }}>%</span>
                </td>
                <td>
                  <button
                    className="ghost"
                    onClick={() => removeDebtItem(d.id)}
                    style={{ padding: '2px 6px', fontSize: 'var(--fs-micro)', color: 'var(--text-soft)' }}
                    title="Delete this debt"
                  >
                    del
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 600 }} colSpan={2}>Total</td>
              <td className="num mono" style={{ fontWeight: 600, color: 'var(--neg)' }}>
                ${formatUsd(total, { decimals: 0 })}
              </td>
              <td colSpan={2}></td>
            </tr>
          </tbody>
        </table>
      )}

      <div className="row" style={{ alignItems: 'center', gap: 'var(--gap-s)', flexWrap: 'wrap' }}>
        <span className="faint mono" style={{ fontSize: 'var(--fs-small)' }}>Add:</span>
        <select
          value={newType}
          onChange={e => setNewType(e.target.value as DebtItemType)}
          style={{ fontSize: 'var(--fs-small)' }}
        >
          {DEBT_TYPES.map(t => (
            <option key={t} value={t}>{DEBT_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="label (optional)"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          style={{ width: 180, fontSize: 'var(--fs-small)' }}
        />
        <input
          type="number"
          step="100"
          placeholder="balance"
          value={newBalance}
          onChange={e => setNewBalance(e.target.value)}
          style={{ width: 110, fontSize: 'var(--fs-small)' }}
        />
        <input
          type="number"
          step="0.25"
          placeholder="APR %"
          value={newApr}
          onChange={e => setNewApr(e.target.value)}
          style={{ width: 80, fontSize: 'var(--fs-small)' }}
        />
        <button onClick={addRow} style={{ fontSize: 'var(--fs-small)' }}>
          Add debt
        </button>
      </div>
    </div>
  );
}
